import { theme, Image, Button, Typography, Spin, message } from "antd";
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router";
import logoImg from "../../../assets/confetti.jpg";
import logo from "../../../assets/logo.png";
import { useMsal } from "@azure/msal-react";
import { isMobile } from "react-device-detect";
import UserContext from "../../Contexts/UserContext";
import { colors } from "../../AdminPortal/utils/colors";
import getDefaultLoginRoutesForRole from "../../Routes/DefaultLoginRoutesForRole";
import { getRoleFromAuth } from "../../../utils/commonUtils";
import debounce from "lodash/debounce";
import { EventType } from "@azure/msal-browser";
import { LoginByRole, doClear } from "../../../utils/manageUserData";
import { USER_TYPES } from "../../Constants/ApplicationConstants";
import {
  ADB2C_WORKFLOW_AUTHORITY_URI,
  ADB2C_WORKFLOW_FORGOT_PASSWORD,
  ADB2C_AGENT_LOGOUT_REDIRECT_URL,
  ADB2C_ADMIN_LOGOUT_REDIRECT_URL,
  BASE_URL,
  PLATFORM_URL,
} from "../../Constants/EnvironmentVariableProvider";
import { RoutesLookup } from "../../Routes/RoutesLookup";
import useSWR from "swr";
import { GET_ACTIVE_WELCOME_IMAGE } from "../../API/paths";

const { Title } = Typography;

const Login = () => {
  const userType =
    window.location.pathname === RoutesLookup.ADMIN_LOGIN
      ? USER_TYPES.ADMIN
      : USER_TYPES.AGENT;
  // Fetch active welcome image using SWR
  const { data: welcomeImageData } = useSWR(
    userType === USER_TYPES.AGENT
      ? { url: `${BASE_URL}${GET_ACTIVE_WELCOME_IMAGE}` }
      : null
  );

  // Use the welcome image from API response or fallback to default
  const welcomeImage =
    userType === USER_TYPES.AGENT
      ? welcomeImageData?.result?.GLIM_Image_Path
      : logoImg;
  const { setAuth, auth } = useContext(UserContext);
  const { instance: msalInstance } = useMsal();
  const [isPageLoading, setPageLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const colorBgLoginLayout = token["colorBgLoginLayout"];

  const addEventCallback = () => {
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        setPageLoading(true);
      }
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        event.payload &&
        event.payload["idToken"]
      ) {
        const account = event.payload["account"];
        localStorage.setItem("account", JSON.stringify(account));
        msalInstance.setActiveAccount(account);
        LoginByRole(
          event.payload["idToken"],
          setAuth,
          navigate,
          msalInstance,
          userType
        );
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setAuth({});
      } else if (
        event.eventType === EventType.LOGIN_FAILURE &&
        event.error &&
        event.error["errorCode"] === "access_denied" &&
        event.error["errorMessage"].includes("AADB2C90118")
      ) {
        const passwordResetAuthority =
          ADB2C_WORKFLOW_AUTHORITY_URI + ADB2C_WORKFLOW_FORGOT_PASSWORD;
        let logoutRedirectUri = ADB2C_AGENT_LOGOUT_REDIRECT_URL;
        if (userType === USER_TYPES.ADMIN) {
          logoutRedirectUri = ADB2C_ADMIN_LOGOUT_REDIRECT_URL;
        } else if (userType === USER_TYPES.AGENT) {
          logoutRedirectUri = ADB2C_AGENT_LOGOUT_REDIRECT_URL;
        }
        msalInstance.loginRedirect({
          scopes: [],
          authority: passwordResetAuthority,
          redirectUri: logoutRedirectUri,
        });
      } else if (
        event.eventType === EventType.LOGIN_FAILURE ||
        (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
          event.error &&
          event.error["errorCode"] === "user_cancelled")
      ) {
        loginCancelled();
      }
    });
  };

  const loginCancelled = debounce(function () {
    msalInstance.logout();
  }, 1500);

  useEffect(() => {
    console.log("log---useffect");
    if (!msalInstance || !msalInstance.getAllAccounts) {
      console.error("MSAL not initialized yet.");
      return;
    }
    if (auth.token) {
      console.log('log---no silent sso');
      navigate(getDefaultLoginRoutesForRole(getRoleFromAuth(auth)))
    }
    else if (userType === USER_TYPES.AGENT){
      window.location.href = `${PLATFORM_URL}/Home`;
      return;
    }
    else if (userType === USER_TYPES.ADMIN) {
      // For admin users, try to restore from localStorage instead of silent SSO
      const checkAdminAuth = async () => {
        console.log("log---checkAdminAuth");
        try {
          const storedAccount = localStorage.getItem('account');
          const storedToken = localStorage.getItem('token');
          
          if (storedAccount && storedToken) {
            const account = JSON.parse(storedAccount);
            const token = JSON.parse(storedToken);
            
            // Set the active account
            msalInstance.setActiveAccount(account);
            
            // Check if token is still valid
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const expirationTime = tokenPayload.exp * 1000;
            const currentTime = Date.now();
            
            if (currentTime < expirationTime) {
              console.log("Admin token is still valid, restoring auth");
              // Trigger the login success flow
              const mockResponse = {
                payload: {
                  account: account,
                  idToken: token
                }
              };
              handleLoginSuccess(mockResponse);
            } else {
              console.log("Admin token expired, clearing auth");
              doClear(msalInstance);
            }
          } else {
            console.log("No stored admin auth found");
            doClear(msalInstance);
          }
        } catch (error) {
          console.error("Admin auth restoration failed", error);
          doClear(msalInstance);
        }
      };
      checkAdminAuth();
    }
    else{
      doClear(msalInstance);
    }
      
    addEventCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleLoginSuccess = (response) => {
    const account = msalInstance.getActiveAccount();
    if (account) {
      navigate(getDefaultLoginRoutesForRole(getRoleFromAuth(auth)));
    }
  };

  const tryLogin = () => {
    setPageLoading(true);
    message.info("Please wait while we are redirecting you to login.");
    msalInstance.handleRedirectPromise().then(() => {
      msalInstance.loginRedirect();
    });
  };
  const handleSignInClick = () => {
    tryLogin();
  };
  return (
    <Spin spinning={isPageLoading}>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          verticalAlign: "middle",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: colorBgLoginLayout,
            width: "55%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            display: isMobile ? "none" : "flex",
          }}
        >
          <div>
            <Image style={{ height: 400 }} preview={false} src={welcomeImage} />
            <Title
              style={{ textAlign: "center", color: colors.black, fontSize: 30 }}
            >
              Woohoo!
            </Title>
            <Title
              level={4}
              style={{ textAlign: "center", color: colors.black, margin: 0 }}
            >
              You have points...
            </Title>
            <Title
              level={4}
              style={{ textAlign: "center", color: colors.black, margin: 0 }}
            >
              Go pick your prize!
            </Title>
          </div>
        </div>
        <div
          style={{
            width: isMobile ? "100%" : "45%",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: 350 }}>
            <div
              style={{ textAlign: "center", position: "relative", top: -30 }}
            >
              <Image
                src={logo}
                preview={false}
                style={{ marginBottom: 20, objectFit: "cover" }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <Title
                level={3}
                style={{
                  marginBottom: 15,
                  marginTop: 0,
                  color: colors.grey900,
                  textAlign: "center",
                }}
              >
                Welcome to Normandy Loyalty Rewards{" "}
                {userType === USER_TYPES.ADMIN ? "Admin Portal" : ""}
              </Title>
            </div>
            {userType === USER_TYPES.AGENT ? (
              <div style={{ textAlign: "center" }}>
                <Spin size="small" style={{ marginRight: 8 }} />
                <span>Redirecting to Normandy Platform...</span>
              </div>
            ) : (
              <Button block type="primary" onClick={handleSignInClick}>
                Sign-in
              </Button>
            )}
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default Login;
